import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { AlertCircle, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(emailOrUsername, password);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map((d) => d.msg || JSON.stringify(d)).join(" "));
      else setError("Login failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1772309748311-94587cae2b37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBlbnRlcnByaXNlJTIwb2ZmaWNlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc2MTAxODQwfDA&ixlib=rb-4.1.0&q=85"
          alt="Enterprise"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0052CC]/80 flex flex-col justify-end p-12">
          <h1
            className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            ARCHON
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Sales Operating System.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <Card className="w-full max-w-md border border-gray-200 shadow-sm rounded-sm">
          <CardContent className="p-8">
            <div className="mb-8">
              <h2
                className="text-2xl font-bold tracking-tight text-[#09090B] mb-1"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Sign In
              </h2>
              <p className="text-sm text-gray-500">Enter your credentials to access the system</p>
            </div>

            {error && (
              <div data-testid="login-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-sm text-red-700">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="emailOrUsername" className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
                  Email or Username
                </Label>
                <Input
                  data-testid="login-email-input"
                  id="emailOrUsername"
                  type="text"
                  value={emailOrUsername}
                  onChange={(e) => setEmailOrUsername(e.target.value)}
                  placeholder="admin@archon.com"
                  className="mt-1.5 rounded-sm h-10 border-gray-300 focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    data-testid="login-password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="rounded-sm h-10 pr-10 border-gray-300 focus:ring-2 focus:ring-[#0052CC] focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm font-medium transition-colors duration-200"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-400 text-center">
                Archon Sales OS v1.0 — Internal Use Only
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
